/**
 * Revalidation Utilities
 *
 * Functions for triggering on-demand revalidation of trail discovery pages
 * when trail data changes. This ensures that ISR pages stay up-to-date.
 *
 * Note: In static export mode, actual revalidation is not possible.
 * These utilities provide graceful fallbacks for static export builds.
 */

/**
 * Detect if we're running in static export mode
 * This is a simple heuristic based on whether we're in a browser environment
 */
const isStaticExport =
  typeof window !== 'undefined' &&
  // In static export, there's no server-side rendering
  (window as any).__NEXT_DATA__?.isFallback === undefined;

/**
 * Trigger revalidation of trail discovery pages
 * @param languages Optional array of language codes to revalidate (defaults to ['en', 'de'])
 * @returns Promise that resolves to the revalidation result
 */
export async function revalidateTrailDiscoveryPages(
  languages: string[] = ['en', 'de']
): Promise<{ success: boolean; message: string }> {
  // In static export mode, we return a message without attempting revalidation
  if (isStaticExport) {
    console.log('Static export mode detected - revalidation not available');
    return {
      success: false,
      message: 'Revalidation not available in static export mode',
    };
  }

  try {
    // Check for environment-specific revalidation token
    const revalidationToken = process.env.NEXT_PUBLIC_REVALIDATION_TOKEN;

    if (!revalidationToken) {
      console.warn('Revalidation token not found in environment variables');
      return {
        success: false,
        message: 'Revalidation token not configured',
      };
    }

    // Determine the base URL (works in both development and production)
    const baseUrl =
      process.env.NEXT_PUBLIC_BASE_URL ||
      (typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost:3000');

    // Call the revalidation API endpoint
    const response = await fetch(`${baseUrl}/api/revalidate-trails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${revalidationToken}`,
      },
      body: JSON.stringify({ languages }),
    });

    // Parse and return the response
    const result = await response.json();

    if (!response.ok) {
      console.error('Revalidation failed:', result);
      return {
        success: false,
        message: result.message || 'Revalidation request failed',
      };
    }

    return {
      success: true,
      message: result.message || 'Revalidation successful',
    };
  } catch (error) {
    console.error('Error triggering revalidation:', error);
    return {
      success: false,
      message:
        error instanceof Error
          ? error.message
          : 'Unknown error during revalidation',
    };
  }
}

/**
 * Trigger revalidation after trail data updates
 * This is a convenience wrapper for use in admin interfaces and services
 * @param trailId The ID of the trail that was updated (for logging purposes)
 * @param languages Optional array of language codes to revalidate
 */
export async function revalidateAfterTrailUpdate(
  trailId: string,
  languages?: string[]
): Promise<void> {
  if (isStaticExport) {
    console.log(
      `Static export mode - skipping revalidation for trail ${trailId}`
    );
    console.log(
      'Note: In static export mode, changes will only appear after a full rebuild'
    );
    return;
  }

  console.log(`Triggering revalidation after update to trail ${trailId}`);

  const result = await revalidateTrailDiscoveryPages(languages);

  if (result.success) {
    console.log(
      `Successfully revalidated trail discovery pages after update to trail ${trailId}`
    );
  } else {
    console.warn(
      `Failed to revalidate trail discovery pages after update to trail ${trailId}: ${result.message}`
    );
  }
}

/**
 * For static export builds, this function provides a message about manual rebuilds
 * This can be used in admin interfaces to inform users about the limitations
 */
export function getRevalidationStatus(): {
  available: boolean;
  mode: string;
  message: string;
} {
  if (isStaticExport) {
    return {
      available: false,
      mode: 'static',
      message:
        'Automatic revalidation is not available in static export mode. Changes will require a manual rebuild.',
    };
  }

  return {
    available: true,
    mode: 'dynamic',
    message:
      'Revalidation is available - changes will be visible after triggering revalidation.',
  };
}
